import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowBack,
  AccountBalance,
  CalendarToday,
  Payment,
  Warning,
  CheckCircle,
  StorefrontOutlined,
  Timeline,
} from '@mui/icons-material';
import { format, parseISO, isValid, differenceInDays } from 'date-fns';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { alpha } from '@mui/material/styles';

interface AccountDetail {
  id: string;
  loandisk_id: string;
  borrower_name: string;
  loan_number: string;
  loan_amount: number;
  amount_paid: number;
  loan_balance: number;
  days_past: number;
  loan_date: string;
  maturity_date: string;
  last_payment: string;
  last_payment_amount: number;
  business: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface BorrowerInfo {
  id: string;
  name: string;
  email: string;
  mobile_no: string;
  address: string;
}

const BorrowerAccountDetails: React.FC = () => {
  const { loandiskId, borrowerId } = useParams<{ loandiskId: string; borrowerId: string }>();
  const [loading, setLoading] = useState<boolean>(true);
  const [accountDetails, setAccountDetails] = useState<AccountDetail[]>([]);
  const [borrowerInfo, setBorrowerInfo] = useState<BorrowerInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { userProfile } = useAuth();
  const isAdmin = userProfile?.role === 'admin';

  // Calculate summary totals
  const totalLoanAmount = accountDetails.reduce((sum, account) => sum + (account.loan_amount || 0), 0);
  const totalAmountPaid = accountDetails.reduce((sum, account) => sum + (account.amount_paid || 0), 0);
  const totalBalance = accountDetails.reduce((sum, account) => sum + (account.loan_balance || 0), 0);
  const activeLoans = accountDetails.filter(account => (account.loan_balance || 0) > 0).length;
  const paidLoans = accountDetails.filter(account => (account.loan_balance || 0) <= 0).length;
  const visibleAccountDetails = isAdmin
    ? accountDetails
    : accountDetails.filter(account => account.status?.trim().toLowerCase() !== 'fully paid');

  useEffect(() => {
    const fetchAccountDetails = async () => {
      if (!loandiskId) {
        setError('No Loandisk ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch account details
        const { data: accountData, error: accountError } = await supabase
          .from('borrower_account_details')
          .select('*')
          .eq('loandisk_id', loandiskId)
          .order('loan_date', { ascending: false });

        if (accountError) throw accountError;

        // Fetch borrower info if borrowerId is provided
        if (borrowerId) {
          const { data: borrowerData, error: borrowerError } = await supabase
            .from('borrowers')
            .select('id, name, email, mobile_no, address')
            .eq('id', borrowerId)
            .single();

          if (borrowerError) {
            console.error('Error fetching borrower info:', borrowerError);
          } else {
            setBorrowerInfo(borrowerData);
          }
        }

        // Calculate days past due for each loan
        const detailsWithDaysPast = accountData.map((detail: any) => {
          let daysPast = detail.days_past || 0;
          if (detail.maturity_date && detail.loan_balance > 0) {
            const maturityDate = parseISO(detail.maturity_date);
            if (isValid(maturityDate)) {
              const today = new Date();
              if (maturityDate < today) {
                daysPast = differenceInDays(today, maturityDate);
              }
            }
          }
          return { ...detail, days_past: daysPast };
        });

        setAccountDetails(detailsWithDaysPast);
      } catch (err) {
        console.error('Error in fetchAccountDetails:', err);
        setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchAccountDetails();
  }, [loandiskId, borrowerId]);

  const handleBack = () => {
    navigate(-1);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      const date = parseISO(dateString);
      return isValid(date) ? format(date, 'MMM dd, yyyy') : 'Invalid date';
    } catch {
      return 'Invalid date';
    }
  };

  const formatCurrency = (amount: number | null) => {
    if (amount === null || amount === undefined) return 'PHP 0.00';
    return `PHP ${amount.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getStatusColor = (status: string | null, balance: number | null) => {
    if (!status) return 'default';
    if (balance === 0 || balance === null) return 'success';
    const statusLower = status.toLowerCase();
    if (statusLower.includes('active')) return 'primary';
    if (statusLower.includes('past due') || statusLower.includes('overdue')) return 'error';
    if (statusLower.includes('restructured')) return 'warning';
    return 'default';
  };

  const getDaysPastColor = (daysPast: number | null) => {
    if (!daysPast || daysPast <= 0) return theme.palette.success.main;
    if (daysPast < 30) return theme.palette.warning.main;
    if (daysPast < 90) return theme.palette.error.main;
    return theme.palette.error.dark;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mb: 2 }}>
          Back
        </Button>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: { xs: 2, sm: 3 } }}>
      <Container maxWidth="lg" sx={{ px: { xs: 1.5, sm: 3 } }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBack}
            sx={{
              mb: 3,
              color: 'text.secondary',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
              },
            }}
          >
            Back to Profile
          </Button>

          <Typography
            variant={isMobile ? "h5" : "h4"}
            sx={{
              fontWeight: 800,
              mb: 1,
              background: 'linear-gradient(45deg, #333333 30%, #666666 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              fontSize: isMobile ? '1.5rem' : '2rem',
              lineHeight: isMobile ? 1.2 : 1.3,
            }}
          >
            Loan Account History
          </Typography>

          {borrowerInfo && (
            <Typography 
              variant="subtitle1" 
              color="text.secondary" 
              sx={{ 
                mb: 3,
                fontSize: isMobile ? '0.875rem' : '1rem',
              }}
            >
              {borrowerInfo.name} • {loandiskId}
            </Typography>
          )}
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} lg={3}>
            <Card 
              sx={{ 
                height: '100%', 
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
                border: '1px solid',
                borderColor: alpha(theme.palette.primary.main, 0.1),
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
                }
              }}
            >
              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box 
                    sx={{ 
                      bgcolor: alpha(theme.palette.primary.main, 0.15),
                      p: isMobile ? 1 : 1.5,
                      borderRadius: 2,
                      mr: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <AccountBalance color="primary" />
                  </Box>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 600, 
                      color: 'text.secondary',
                      fontSize: isMobile ? '0.875rem' : '1rem',
                    }}
                  >
                    Total Balance
                  </Typography>
                </Box>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                    fontFamily: 'monospace',
                    letterSpacing: '-0.5px',
                    fontSize: isMobile ? '1.5rem' : '2rem',
                  }}
                >
                  {totalBalance.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            <Card 
              sx={{ 
                height: '100%', 
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)} 0%, ${alpha(theme.palette.success.main, 0.1)} 100%)`,
                border: '1px solid',
                borderColor: alpha(theme.palette.success.main, 0.1),
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 8px 24px ${alpha(theme.palette.success.main, 0.15)}`,
                }
              }}
            >
              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box 
                    sx={{ 
                      bgcolor: alpha(theme.palette.success.main, 0.15),
                      p: isMobile ? 1 : 1.5,
                      borderRadius: 2,
                      mr: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Payment color="success" />
                  </Box>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 600, 
                      color: 'text.secondary',
                      fontSize: isMobile ? '0.875rem' : '1rem',
                    }}
                  >
                    Amount Paid
                  </Typography>
                </Box>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700,
                    color: theme.palette.success.main,
                    fontFamily: 'monospace',
                    letterSpacing: '-0.5px',
                    fontSize: isMobile ? '1.5rem' : '2rem',
                  }}
                >
                  {isAdmin ? totalAmountPaid.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '******'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            <Card 
              sx={{ 
                height: '100%', 
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.05)} 0%, ${alpha(theme.palette.info.main, 0.1)} 100%)`,
                border: '1px solid',
                borderColor: alpha(theme.palette.info.main, 0.1),
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 8px 24px ${alpha(theme.palette.info.main, 0.15)}`,
                }
              }}
            >
              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box 
                    sx={{ 
                      bgcolor: alpha(theme.palette.info.main, 0.15),
                      p: isMobile ? 1 : 1.5,
                      borderRadius: 2,
                      mr: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Timeline color="info" />
                  </Box>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 600, 
                      color: 'text.secondary',
                      fontSize: isMobile ? '0.875rem' : '1rem',
                    }}
                  >
                    Total Loans
                  </Typography>
                </Box>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700,
                    color: theme.palette.info.main,
                    fontFamily: 'monospace',
                    letterSpacing: '-0.5px',
                    fontSize: isMobile ? '1.5rem' : '2rem',
                  }}
                >
                  {isAdmin ? totalLoanAmount.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '******'}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mt: 1.5,
                    color: alpha(theme.palette.info.main, 0.8),
                    fontWeight: 500,
                    fontSize: isMobile ? '0.875rem' : '1rem',
                  }}
                >
                  {visibleAccountDetails.length} loan{visibleAccountDetails.length !== 1 ? 's' : ''}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            <Card 
              sx={{ 
                height: '100%', 
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.05)} 0%, ${alpha(theme.palette.warning.main, 0.1)} 100%)`,
                border: '1px solid',
                borderColor: alpha(theme.palette.warning.main, 0.1),
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 8px 24px ${alpha(theme.palette.warning.main, 0.15)}`,
                }
              }}
            >
              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box 
                    sx={{ 
                      bgcolor: alpha(theme.palette.warning.main, 0.15),
                      p: isMobile ? 1 : 1.5,
                      borderRadius: 2,
                      mr: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <CheckCircle color="warning" />
                  </Box>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 600, 
                      color: 'text.secondary',
                      fontSize: isMobile ? '0.875rem' : '1rem',
                    }}
                  >
                    Loan Status
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={`${activeLoans} Active`}
                    color="primary"
                    size="small"
                    sx={{ 
                      fontWeight: 600,
                      px: 1,
                      borderRadius: '8px',
                      background: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      border: '1px solid',
                      borderColor: alpha(theme.palette.primary.main, 0.2),
                      fontSize: isMobile ? '0.875rem' : '1rem',
                    }}
                  />
                  {isAdmin && (
                    <Chip
                      label={`${paidLoans} Paid`}
                      color="success"
                      size="small"
                      sx={{
                        fontWeight: 600,
                        px: 1,
                        borderRadius: '8px',
                        background: alpha(theme.palette.success.main, 0.1),
                        color: theme.palette.success.main,
                        border: '1px solid',
                        borderColor: alpha(theme.palette.success.main, 0.2),
                        fontSize: isMobile ? '0.875rem' : '1rem',
                      }}
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Loan Details Table/Cards */}
        {visibleAccountDetails.length > 0 ? (
          isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {visibleAccountDetails.map((account) => (
                <Card
                  key={account.id}
                  sx={{
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: theme.shadows[4],
                    },
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {account.loan_number}
                      </Typography>
                      <Chip
                        label={account.status || (account.loan_balance <= 0 ? 'Paid' : 'Active')}
                        size="small"
                        color={getStatusColor(account.status, account.loan_balance)}
                        sx={{ fontWeight: 500 }}
                      />
                    </Box>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Loan Date
                        </Typography>
                        <Typography variant="body2">
                          {formatDate(account.loan_date)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Maturity Date
                        </Typography>
                        <Typography variant="body2">
                          {formatDate(account.maturity_date)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Loan Amount
                        </Typography>
                        <Typography variant="body2">
                          {formatCurrency(account.loan_amount)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Amount Paid
                        </Typography>
                        <Typography variant="body2">
                          {formatCurrency(account.amount_paid)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Balance
                        </Typography>
                        <Typography 
                          variant="body2"
                          sx={{
                            fontWeight: 'bold',
                            color: account.loan_balance > 0 ? theme.palette.error.main : theme.palette.success.main,
                          }}
                        >
                          {formatCurrency(account.loan_balance)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Days Past Due
                        </Typography>
                        <Typography 
                          variant="body2"
                          sx={{
                            fontWeight: 'bold',
                            color: getDaysPastColor(account.days_past),
                          }}
                        >
                          {account.days_past > 0 ? account.days_past : 0}
                        </Typography>
                      </Grid>
                      {account.last_payment && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">
                            Last Payment
                          </Typography>
                          <Typography variant="body2">
                            {formatDate(account.last_payment)} • {formatCurrency(account.last_payment_amount)}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: theme.shadows[1] }}>
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Loan Number</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Maturity Date</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'text.primary' }}>Loan Amount</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'text.primary' }}>Amount Paid</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'text.primary' }}>Balance</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Last Payment</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'text.primary' }}>Days Past Due</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleAccountDetails.map((account) => (
                      <TableRow
                        key={account.id}
                        hover
                        sx={{
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                          },
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {account.loan_number}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{formatDate(account.loan_date)}</TableCell>
                        <TableCell>{formatDate(account.maturity_date)}</TableCell>
                        <TableCell align="right">{formatCurrency(account.loan_amount)}</TableCell>
                        <TableCell align="right">{formatCurrency(account.amount_paid)}</TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 'bold',
                            color: account.loan_balance > 0 ? theme.palette.error.main : theme.palette.success.main,
                          }}
                        >
                          {formatCurrency(account.loan_balance)}
                        </TableCell>
                        <TableCell>
                          {account.last_payment ? (
                            <Box>
                              <Typography variant="body2">{formatDate(account.last_payment)}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatCurrency(account.last_payment_amount)}
                              </Typography>
                            </Box>
                          ) : (
                            'N/A'
                          )}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 'bold',
                            color: getDaysPastColor(account.days_past),
                          }}
                        >
                          {account.days_past > 0 ? account.days_past : 0}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={account.status || (account.loan_balance <= 0 ? 'Paid' : 'Active')}
                            size="small"
                            color={getStatusColor(account.status, account.loan_balance)}
                            sx={{ fontWeight: 500 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )
        ) : (
          <Alert
            severity="info"
            sx={{
              borderRadius: 2,
              bgcolor: alpha(theme.palette.info.main, 0.08),
              border: '1px solid',
              borderColor: alpha(theme.palette.info.main, 0.12),
            }}
          >
            No active loan account details found for this borrower.
          </Alert>
        )}
      </Container>
    </Box>
  );
};

export default BorrowerAccountDetails;
